import {
  assemblyClientDomainStaging,
  assemblyDashboardHostnameProd,
  assemblyDashboardHostnameStaging,
  copilotClientDomainStaging,
} from 'src/constants/middlewareConsts';
import { STUDIO_INTERNAL_PORTAL_TAB_NAME } from 'src/constants/studioFtux';

const STAGING_CLIENT_DOMAINS = [
  assemblyClientDomainStaging,
  copilotClientDomainStaging,
];
const LOCAL_HOSTNAME = 'localhost';
// Local "Sign in as client" links open on the loopback IP so the client session
// doesn't share cookies with the dashboard on localhost.
const LOCAL_CLIENT_PREVIEW_HOSTNAME = '127.0.0.1';
// Railway PR previews pair portal-{repo}-pr-{n} with dashboard-{repo}-pr-{n}.
const RAILWAY_PREVIEW_DOMAIN = '.up.railway.app';
const RAILWAY_PORTAL_PREFIX = 'portal-';
const RAILWAY_DASHBOARD_PREFIX = 'dashboard-';

const OPEN_ADD_APP_MESSAGE_TYPE = 'assembly:open-add-app';
const ADD_APP_READY_MESSAGE_TYPE = 'assembly:add-app-ready';
// The dashboard replies as soon as it has covered its page, so this only matters
// when it can't reply; it stays well inside the ~5s a browser still treats the
// click as recent enough to switch tabs on.
const ADD_APP_READY_TIMEOUT_MS = 3000;

const isOpenTab = (value: unknown): value is Window =>
  typeof value === 'object' &&
  value !== null &&
  'closed' in value &&
  value.closed === false &&
  'postMessage' in value;

const isMessageOfType = (data: unknown, type: string): boolean =>
  typeof data === 'object' &&
  data !== null &&
  'type' in data &&
  data.type === type;

/** Whether a `message` event is the client preview asking for Add App. */
export const isOpenAddAppMessage = (data: unknown): boolean =>
  isMessageOfType(data, OPEN_ADD_APP_MESSAGE_TYPE);

/** Tells the client preview that asked for Add App that this tab can be shown. */
export const replyAddAppReady = (
  source: MessageEventSource | null,
  origin: string,
): void => {
  if (isOpenTab(source)) {
    source.postMessage({ type: ADD_APP_READY_MESSAGE_TYPE }, origin);
  }
};

/**
 * Switches back to the CRM tab that opened this client preview. Returns false when
 * there's no live tab to switch to: no opener (not launched from the CRM), a closed
 * one, or one the browser can no longer reach by name.
 */
export const switchToInternalPortalTab = (): boolean => {
  if (!isOpenTab(window.opener)) {
    return false;
  }
  // opener.focus() alone is a no-op in Chrome (focus-stealing prevention).
  // Re-targeting the Internal Portal tab by the name the CRM page stamped on
  // it switches the browser to that existing tab (empty URL = don't navigate
  // it); focusing the returned handle brings it forward.
  const internalTab = window.open('', STUDIO_INTERNAL_PORTAL_TAB_NAME);
  // If the CRM tab navigated cross-origin after opening this preview, the
  // browser clears its window.name, so the name no longer resolves and the
  // call above spawns a fresh blank tab instead of switching back. The opener
  // is the CRM tab, so when the name still resolves the handle is that same
  // context (reference equality holds even cross-origin — no property read);
  // otherwise close the stray tab so we truly no-op.
  if (internalTab && internalTab !== window.opener) {
    internalTab.close();
    return false;
  }
  internalTab?.focus();
  return internalTab !== null;
};

/**
 * Asks the CRM tab that opened this client preview to open Add App. The tab is on
 * another domain, so pointing it at a URL would reload the whole dashboard; a
 * message lets it navigate in-app instead. Resolves once the tab has covered its
 * current page (or after a timeout), so switching to it never shows CRM first.
 * Null when there's no live tab to ask.
 */
export const requestAddAppInInternalPortalTab = (): Promise<void> | null => {
  const { opener } = window;
  if (!isOpenTab(opener)) {
    return null;
  }
  const dashboardOrigin = getDashboardOrigin(window.location);
  const listeners = new AbortController();
  const addAppReady = new Promise<void>((resolve) => {
    window.addEventListener(
      'message',
      (event: MessageEvent<unknown>) => {
        if (
          event.source === opener &&
          event.origin === dashboardOrigin &&
          isMessageOfType(event.data, ADD_APP_READY_MESSAGE_TYPE)
        ) {
          resolve();
        }
      },
      { signal: listeners.signal },
    );
    window.setTimeout(resolve, ADD_APP_READY_TIMEOUT_MS);
  });
  // Targeting the dashboard origin drops the message if the tab has since left it.
  opener.postMessage({ type: OPEN_ADD_APP_MESSAGE_TYPE }, dashboardOrigin);
  return addAppReady.finally(() => listeners.abort());
};

/**
 * The dashboard for the client portal this tab is on. Client portals live on their
 * own domain, so a staging client domain means the staging dashboard, a Railway
 * preview its paired dashboard, and anything else (including a custom domain) prod.
 * Local dev serves the dashboard on localhost, the same port as the preview.
 */
export const getDashboardOrigin = ({
  hostname,
  origin,
  protocol,
  port,
}: Pick<Location, 'hostname' | 'origin' | 'protocol' | 'port'>): string => {
  if (hostname === LOCAL_HOSTNAME || hostname.endsWith(`.${LOCAL_HOSTNAME}`)) {
    return origin;
  }
  if (hostname === LOCAL_CLIENT_PREVIEW_HOSTNAME) {
    return `${protocol}//${LOCAL_HOSTNAME}${port ? `:${port}` : ''}`;
  }
  if (
    hostname.endsWith(RAILWAY_PREVIEW_DOMAIN) &&
    hostname.startsWith(RAILWAY_PORTAL_PREFIX)
  ) {
    return `https://${RAILWAY_DASHBOARD_PREFIX}${hostname.slice(RAILWAY_PORTAL_PREFIX.length)}`;
  }
  const isStaging = STAGING_CLIENT_DOMAINS.some(
    (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
  );
  return `https://${
    isStaging ? assemblyDashboardHostnameStaging : assemblyDashboardHostnameProd
  }`;
};
